/**
 * V104-G-10: the stored law text, as a searchable reference pane for the
 * rule-authoring form. Rendered strictly as text (React text nodes inside a
 * pre-wrapped block; never HTML). Search is a plain case-insensitive match
 * with a count and previous / next; the located quote is highlighted and
 * scrolled into view. Selecting text offers "Use as quote for …" so a quote
 * is copied exactly.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Field, Input, Select } from '@aigovops/design-system';

const MAX_HITS = 500;

export interface QuoteTarget {
  id: string;
  label: string;
}

export interface LawTextPaneProps {
  text: string;
  /** Plain words for where the text came from ("From the law source" / "Added by an editor"). */
  originLabel: string;
  /** The quote to highlight (raw indices) and a counter that bumps on every "Find". */
  focusRange: { start: number; end: number; label: string; n: number } | null;
  quoteTargets: QuoteTarget[];
  /** The quote field the editor used last; the "Use as quote" target starts there. */
  defaultTarget?: string | null;
  onUseSelection: (targetId: string, text: string) => void;
  headingId?: string;
}

type Mark = { start: number; end: number; kind: 'hit' | 'current' | 'quote' };

/** The sentence around [start, end): from the previous sentence end (or line break) to the next. */
export function sentenceAround(text: string, start: number, end: number): string {
  let a = start;
  while (a > 0 && !/[.;\n]/.test(text[a - 1]!)) a--;
  let b = end;
  while (b < text.length && !/[.;\n]/.test(text[b]!)) b++;
  return text.slice(a, Math.min(text.length, b + 1)).trim().slice(0, 4000);
}

export function LawTextPane({ text, originLabel, focusRange, quoteTargets, defaultTarget, onUseSelection, headingId = 'law-text-h' }: LawTextPaneProps) {
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState(0);
  const [selection, setSelection] = useState('');
  const [target, setTarget] = useState(defaultTarget ?? quoteTargets[0]?.id ?? '');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [announce, setAnnounce] = useState('');

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [] as Array<{ start: number; end: number }>;
    const lower = text.toLowerCase();
    const out: Array<{ start: number; end: number }> = [];
    let at = lower.indexOf(q);
    while (at >= 0 && out.length < MAX_HITS) {
      out.push({ start: at, end: at + q.length });
      at = lower.indexOf(q, at + q.length);
    }
    return out;
  }, [text, query]);

  useEffect(() => setCurrent(0), [query]);
  useEffect(() => {
    if (defaultTarget && quoteTargets.some((t) => t.id === defaultTarget)) setTarget(defaultTarget);
  }, [defaultTarget]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!quoteTargets.some((t) => t.id === target)) setTarget(quoteTargets[0]?.id ?? '');
  }, [quoteTargets, target]);

  const marks = useMemo(() => {
    const m: Mark[] = hits.map((h, i) => ({ ...h, kind: i === current ? 'current' : 'hit' }));
    if (focusRange) m.push({ start: focusRange.start, end: focusRange.end, kind: 'quote' });
    return m;
  }, [hits, current, focusRange]);

  // Split the text at every mark boundary; each segment carries its strongest mark.
  const segments = useMemo(() => {
    const cuts = new Set<number>([0, text.length]);
    for (const m of marks) {
      cuts.add(m.start);
      cuts.add(m.end);
    }
    const points = [...cuts].sort((a, b) => a - b);
    const rank = { quote: 1, hit: 2, current: 3 } as const;
    const out: Array<{ start: number; end: number; kind: Mark['kind'] | null }> = [];
    for (let i = 0; i < points.length - 1; i++) {
      const s = points[i]!;
      const e = points[i + 1]!;
      let kind: Mark['kind'] | null = null;
      for (const m of marks) if (m.start <= s && m.end >= e && (!kind || rank[m.kind] > rank[kind])) kind = m.kind;
      out.push({ start: s, end: e, kind });
    }
    return out;
  }, [text, marks]);

  const scrollTo = (selector: string) => {
    const el = scrollRef.current?.querySelector<HTMLElement>(selector);
    if (el && scrollRef.current) {
      const box = scrollRef.current;
      box.scrollTop = el.offsetTop - box.offsetTop - box.clientHeight / 3;
    }
  };

  useEffect(() => {
    if (!focusRange) return;
    scrollTo('[data-mark="quote"]');
    const excerpt = text.slice(focusRange.start, Math.min(focusRange.end, focusRange.start + 80));
    setAnnounce(`${focusRange.label}: found in the law text, “${excerpt}${focusRange.end - focusRange.start > 80 ? '…' : ''}”`);
  }, [focusRange?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hits.length) scrollTo('[data-mark="current"]');
  }, [current, hits.length]);

  useEffect(() => {
    if (query.trim().length < 2) return;
    const t = setTimeout(() => setAnnounce(hits.length ? `${hits.length} match${hits.length === 1 ? '' : 'es'}.` : `No matches for ${query.trim()}.`), 400);
    return () => clearTimeout(t);
  }, [query, hits.length]);

  const step = (d: number) => {
    if (!hits.length) return;
    const next = (current + d + hits.length) % hits.length;
    setCurrent(next);
    setAnnounce(`Match ${next + 1} of ${hits.length}.`);
  };

  const readSelection = () => {
    const sel = window.getSelection();
    const box = scrollRef.current;
    if (!sel || !box || sel.isCollapsed || !sel.anchorNode || !box.contains(sel.anchorNode)) {
      setSelection('');
      return;
    }
    setSelection(sel.toString().trim().slice(0, 4000));
  };

  const count = query.trim().length < 2 ? '' : hits.length === 0 ? 'No matches' : `${current + 1} of ${hits.length}${hits.length === MAX_HITS ? '+' : ''}`;

  return (
    <section aria-labelledby={headingId} style={{ display: 'grid', gap: 'var(--space-2)', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <h2 id={headingId} style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Law text
        </h2>
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{originLabel}</span>
      </div>
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          step(1);
        }}
        style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field id="law-search" label="Search the law text" hint={count || undefined}>
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.shiftKey) {
                  e.preventDefault();
                  step(-1);
                }
              }}
              autoComplete="off"
            />
          </Field>
        </div>
        <Button type="button" compact variant="secondary" onClick={() => step(-1)} aria-disabled={!hits.length || undefined} aria-label="Previous match">
          Prev
        </Button>
        <Button type="submit" compact variant="secondary" aria-disabled={!hits.length || undefined} aria-label="Next match">
          Next
        </Button>
      </form>
      {(selection || hits.length) && quoteTargets.length ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '10rem' }}>
            <Field id="law-use-target" label={selection ? 'Use the selected text as the quote for' : 'Use the sentence with this match as the quote for'}>
              <Select value={target} onChange={(e) => setTarget(e.target.value)}>
                {quoteTargets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Button
            type="button"
            compact
            variant="primary"
            onClick={() => {
              const h = hits[current];
              const value = selection || (h ? sentenceAround(text, h.start, h.end) : '');
              if (!value) return;
              onUseSelection(target, value);
              setSelection('');
            }}
          >
            {selection ? 'Use as quote' : 'Use this sentence'}
          </Button>
        </div>
      ) : null}
      <span id="law-text-help" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Select a passage to use it as a quote, or search and use the sentence with the match.
      </span>
      <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {announce}
      </div>
      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-label="Law text"
        aria-describedby="law-text-help"
        onMouseUp={readSelection}
        onKeyUp={readSelection}
        style={{
          overflowY: 'auto',
          maxHeight: 'min(70vh, 48rem)',
          padding: 'var(--space-3)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-2xs)',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {segments.map((s) =>
          s.kind ? (
            <mark
              key={s.start}
              data-mark={s.kind}
              style={{
                color: 'var(--text)',
                background: s.kind === 'quote' ? 'var(--accent-soft)' : 'var(--warning-subtle)',
                outline: s.kind === 'current' ? '2px solid var(--text)' : undefined,
                boxShadow: s.kind === 'quote' ? 'inset 0 -2px 0 var(--accent)' : s.kind === 'hit' ? 'inset 0 -2px 0 var(--text-muted)' : undefined,
              }}
            >
              {text.slice(s.start, s.end)}
            </mark>
          ) : (
            <span key={s.start}>{text.slice(s.start, s.end)}</span>
          )
        )}
      </div>
    </section>
  );
}
